import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { E_USER_ROLE, RootState } from 'types';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import brushIcon from 'assets/icons/brush.svg';
import menuDelete from 'assets/icons/menu-delete.svg';
import menuEdit from 'assets/icons/menu-edit.svg';
import DownloadIcon from 'assets/icons/download-icon-white.svg';
import cx from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import ViewIcon from 'assets/icons/view-icon-white.svg';
import { selectSessionUserRole } from 'app/slice/selectors';
import { translations as _ } from '../../../../../locales/translations';
import { gaPageView } from 'utils/ga';
import CustomHeader from 'app/components/CustomHeader';
import { useDashboardSlice } from '../../slice';
import {
  selectDeleteMedia,
  selectGuidelineURL,
  selectProductDetail,
} from '../../slice/selectors';
import { toast } from 'react-toastify';
import MediaItem from './MediaItem';

export default function DetailRightPart(props: any) {
  const { t } = useTranslation();
  const i18nKey = _.productDetail;
  const { actions } = useDashboardSlice();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isMenuDeleteOpen, setIsMenuDeleteOpen] = useState(false);
  const [countItems, setCountItems] = useState<any>(0);
  const location = useLocation<{ productCode: string }>();

  const [currentProductCode, setCurrentProductCode] = useState<string>(
    location.state.productCode,
  );

  const [currentProductPosition, setCurrentProductPosition] = useState<
    number
  >();

  const role = useSelector(selectSessionUserRole);
  const methods = useForm({
    mode: 'onSubmit',
  });

  const { handleSubmit, watch, setValue, getValues } = methods;

  let { data: productDetail, loading: loadingProductDetail } = useSelector(
    selectProductDetail,
  );

  let { data: guideline, loading: guidelineUrlLoading } = useSelector(
    selectGuidelineURL,
  );

  let { success: mediaDeletedSuccess, loading: mediaLoading } = useSelector(
    selectDeleteMedia,
  );

  const productList = useSelector(
    (state: RootState) => state.dashboard?.getProducts.data,
  );
  const watchAllFields = watch();

  function onSubmit(data) {
    if (Object.keys(watchAllFields).length && productDetail?.id) {
      const idsToBeDeleted = Object.keys(watchAllFields.files)
        .map(o =>
          watchAllFields.files[o]
            .filter(v => !!v.value)
            .map(a => a.value)
            .reduce((acc, current) => {
              acc.push(current);
              return acc;
            }, []),
        )
        .flat(1)
        .map(n => +n);

      dispatch(
        actions.deleteMedia({
          productId: productDetail?.id.toString(),
          ids: idsToBeDeleted,
        }),
      );
    }
  }

  useEffect(() => {
    if (Object.keys(watchAllFields).length) {
      setCountItems(
        Object.keys(watchAllFields.files)
          .map(o => watchAllFields.files[o].filter(v => !!v.value).length)
          .reduce((accumulator, currentValue) => accumulator + currentValue, 0),
      );
    }
  }, [watchAllFields]);

  useEffect(() => {
    dispatch(actions.getGuidelineURL());
    dispatch(actions.getProducts());
    gaPageView(window.location.pathname + window.location.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, dispatch]);

  useEffect(() => {
    const handleBackButton = event => {
      if (window.location.pathname === '/product-detail/:id') {
        history.push('/');
      }
    };
    window.onpopstate = handleBackButton;

    return () => {
      window.onpopstate = null;
    };
  }, [history]);

  useEffect(() => {
    if (mediaDeletedSuccess) {
      toast.info(t(i18nKey.deletedFiles));
      dispatch(actions.resetFilesDeleted());
      getProductDetail(currentProductCode);
      setIsMenuDeleteOpen(false);

      if (getValues(`files`)) {
        let newObj = {};
        Object.keys(getValues(`files`)).forEach(d => {
          newObj[d] = getValues(`files`)[d].map(m => {
            return {
              ...m,
              value: false,
            };
          });
        });
        setValue(`files`, newObj);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaDeletedSuccess]);

  function getProductDetail(productCode) {
    dispatch(actions.getProductDetail(productCode));
    setCurrentProductCode(productCode);
    setCurrentProductPosition(getProductIndex(productCode));
  }

  function getProductIndex(productCode) {
    return productList?.findIndex(obj => obj.product_code === productCode);
  }

  return (
    <FormProvider {...methods}>
      <form className="form-acs" onSubmit={handleSubmit(onSubmit)}>
        <div
          className={cx('product-detail-content', {
            isMenuDeleteOpen: isMenuDeleteOpen && countItems,
          })}
        >
          <div className="product-detail-header">
            <CustomHeader addBack urlBack={'/'} showLink={true} />
          </div>
          <div className="product-detail-subheader">
            <h2>Documentation</h2>
            {role === E_USER_ROLE.ADMIN ? (
              <div className="product-detail-subheader-control">
                <button
                  className={`product-detail-subheader-control-delete`}
                  onClick={e => {
                    e.preventDefault();
                    setIsMenuDeleteOpen(!isMenuDeleteOpen);
                  }}
                >
                  <img alt="delete" src={menuDelete} />
                </button>
                <button
                  className={'product-detail-subheader-control-edit'}
                  onClick={e => {
                    if (productDetail) {
                      dispatch(actions.fillProductInEdit(productDetail));
                      history.push('/product-edit');
                      e.preventDefault();
                    }
                  }}
                >
                  <img alt="edit" src={menuEdit} />
                </button>
              </div>
            ) : null}
          </div>
          {productDetail?.mediaFolders.map((mediaFolder, id) => {
            return (
              <MediaItem
                key={mediaFolder.code}
                mediaKey={mediaFolder.code}
                mediaElements={mediaFolder}
                menuDeleteOpen={isMenuDeleteOpen}
              />
            );
          })}

          {!guidelineUrlLoading && guideline?.url && (
            <div className="media-item product-detail-styles">
              <div className="media-item-container">
                <div className="media-item-header">
                  <div className="media-item-header-content">
                    <img alt="styles" className="brush" src={brushIcon} />
                    <h4> {t(i18nKey.guideline)}</h4>
                    <a href={guideline.url} target={'_BLANK'} rel="noreferrer">
                      <img
                        alt="view guideline"
                        src={ViewIcon}
                        className="download view"
                      />
                    </a>
                    <a
                      href={guideline.url}
                      target={'_BLANK'}
                      rel="noreferrer"
                      download
                    >
                      <img
                        alt="download guideline"
                        src={DownloadIcon}
                        className="download"
                      />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!mediaLoading && isMenuDeleteOpen && countItems ? (
            <div className="product-detail-action">
              {countItems ? (
                <div className="product-detail-action-item">
                  {countItems} {t(i18nKey.item)}
                </div>
              ) : (
                ''
              )}
              <div
                className="product-detail-action-cancel"
                onClick={() => setIsMenuDeleteOpen(false)}
              >
                {t(i18nKey.cancel)}
              </div>
              <div className="product-detail-action-delete">
                <button type="submit">{t(i18nKey.delete)}</button>
              </div>
            </div>
          ) : (
            ''
          )}
          {mediaLoading && isMenuDeleteOpen && (
            <div className="product-detail-action">{t(i18nKey.deleting)}</div>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
